import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import {
  classes,
  Form,
  OutlinedButton,
  OutlinedInput,
  PrimaryButton,
  PrivacyCheckbox,
  PrivacyDisclaimer,
  requiresExplicitConsent,
} from '@bloomberg/media-ui-core';

import {
  setButtonTypeFromVariant,
  setThemeFromVariant,
} from '../utils/helpers';

import styles from './styles.module.css';

// in ms for setTimeout
const DEFAULT_TYPING_WAIT_TIME = 500;
const EMAIL_REGEX =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

export const EMAIL_ERROR_MESSAGE = 'Please enter a valid email address';

export interface Props extends GlobalProps {
  inputId?: string;
  inlineButton?: boolean;
  countryCode?: string;
  centeredLayout?: boolean;
  buttonText?: string;
  loading?: boolean;
  onFormSubmit: (email: string, marketingConsent: boolean) => void;
  onEmailChange?: (email: string) => void;
  error?: string;
  autoFocus?: boolean;
  hidePrivacyPolicy?: boolean;
  defaultValue?: string;
  /** @deprecated use countryCode */
  isAutoOptedIn?: boolean;
  /** @deprecated use theme and buttonType */
  variant?: 'default' | 'dark';
  buttonType?: 'primary' | 'outlined';
  children?: React.ReactNode;
  autocomplete?: string;
  theme?: 'light' | 'dark';
  setGlobalMarketingConsent?: (marketingConsent: boolean) => void;
  emailVerificationWaitTime?: number;
  fullWidth?: boolean;
}

const isBadEmail = (email: string): boolean => {
  if (!email) {
    return false;
  }

  return !EMAIL_REGEX.test(email);
};

export const shouldAutoOptIn = (countryCode: string | undefined): boolean => {
  return !requiresExplicitConsent(countryCode);
};

export const EmailForm = React.forwardRef<HTMLInputElement, Props>(
  function EmailForm(props: Props, ref) {
    const {
      inlineButton = false,
      inputId = 'email-form-input',
      onFormSubmit,
      onEmailChange,
      centeredLayout,
      buttonText = 'Sign Up',
      loading = false,
      error,
      countryCode,
      autoFocus = false,
      hidePrivacyPolicy = false,
      defaultValue = '',
      className,
      isAutoOptedIn = false,
      children = null,
      autocomplete,
      setGlobalMarketingConsent,
      emailVerificationWaitTime = DEFAULT_TYPING_WAIT_TIME,
      fullWidth = true,
    } = props;

    const theme = setThemeFromVariant(props.theme, props.variant);
    const buttonType = setButtonTypeFromVariant(
      props.buttonType,
      props.variant
    );

    const [inputValue, setInputValue] = useState(
      defaultValue.trim().toLowerCase()
    );
    const [showCheckmark, setShowCheckmark] = useState(false);
    const [optIn, setOptIn] = useState(
      shouldAutoOptIn(countryCode) || isAutoOptedIn
    );
    const [isInFocus, setIsInFocus] = useState(false);
    const [showError, setShowError] = useState(false);
    const [typingValidationTimer, setTypingValidationTimer] = useState<
      undefined | NodeJS.Timeout
    >(undefined);
    const inputValueRef = useRef(inputValue);

    const isActive = inputValue || isInFocus;

    useEffect(() => {
      setGlobalMarketingConsent?.(optIn);
    }, [optIn]);

    const isEmailValidAndUpdate = (
      email: string,
      onlySetOnValid = false
    ): boolean => {
      const isEmailBad = isBadEmail(email);
      const emailIsEmpty = email.length === 0;
      const isEmailValid = !emailIsEmpty && !isEmailBad;

      if (onlySetOnValid) {
        if (isEmailValid) {
          setShowCheckmark(true);
          setShowError(false);
          setTypingValidationTimer(undefined);
        }
      } else {
        setShowCheckmark(!isEmailBad && !emailIsEmpty);
        setShowError(isEmailBad);
        setTypingValidationTimer(undefined);
      }

      return isEmailValid;
    };

    useEffect(() => {
      // need this to use latest value in timer
      inputValueRef.current = inputValue;

      // this is linear time, could use inputValue too
      if (isEmailValidAndUpdate(inputValueRef.current, true)) {
        return;
      }

      if (typingValidationTimer) {
        clearTimeout(typingValidationTimer);
        setTypingValidationTimer(undefined);
      } else {
        setShowError(false);
      }

      const emailCheckTimer = setTimeout(() => {
        // this is the latest value of inputValue, not the one updated DEFAULT_TYPING_WAIT_TIME ago when the timer started
        isEmailValidAndUpdate(inputValueRef.current);
      }, emailVerificationWaitTime);

      setTypingValidationTimer(emailCheckTimer);

      onEmailChange?.(inputValue);
    }, [inputValue]);

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const val = e.target.value.trim().toLowerCase();

      setInputValue(val);
    };

    const onFocus = useCallback((): void => {
      setIsInFocus(true);
    }, [setIsInFocus]);

    const submitForm = useCallback(
      (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        // Default client side validation is disabled so validation is done here and blocks submission
        const emailIsBad = isBadEmail(inputValue);
        const emailIsEmpty = inputValue.trim().length === 0;

        setShowError(emailIsBad);
        setShowCheckmark(!emailIsBad && !emailIsEmpty);

        if (inputValue && !emailIsBad) {
          onFormSubmit(inputValue, optIn);
        }
      },
      [onFormSubmit, inputValue, optIn]
    );

    const onCheckboxChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setOptIn(event.target.checked);
      },
      [setOptIn]
    );

    const inputClasses = classes([
      styles.textInput,
      // ...(inlineButton ? [styles.inlineInput] : []),
      isActive ? styles.activeState : styles.inactiveState,
      {
        [styles.darkInput]: theme === 'dark',
      },
    ]);
    const formClasses = classes([
      ...(className ? [className] : []),
      { [styles.centeredLayout]: centeredLayout },
      { [styles.maxWidth]: !fullWidth },
      styles.content,
    ]);

    const variantStyles = theme === 'dark' ? styles.dark : '';

    const buttonClasses = classes([
      inlineButton ? styles.inlineButton : styles.standardButton,
      styles.noMargin,
    ]);

    const textInput = (
      <OutlinedInput
        id={inputId}
        ref={ref}
        hideLabel
        type="email"
        className={inputClasses}
        placeholder="Enter your email"
        value={inputValue}
        required={true}
        error={showError}
        success={showCheckmark}
        helperText={showError ? EMAIL_ERROR_MESSAGE : undefined}
        inputProps={{ onFocus, autoFocus }}
        onChange={onInputChange}
        variant={theme === 'dark' ? 'dark' : 'default'}
        autocomplete={autocomplete}
      />
    );

    const submitButton = useMemo(
      () =>
        buttonType === 'primary' ? (
          <PrimaryButton
            type="submit"
            fullWidth={true}
            loading={loading}
            size={inlineButton ? 'default' : 'small'}
            className={buttonClasses}
            version="phoenix"
            colorMode={theme === 'dark' ? 'dark' : 'light'}
            disabled={!showCheckmark}
          >
            {buttonText}
          </PrimaryButton>
        ) : (
          <OutlinedButton
            type="submit"
            loading={loading}
            size={inlineButton ? 'default' : 'small'}
            fullWidth={true}
            version="phoenix"
            colorMode={theme === 'dark' ? 'dark' : 'light'}
            className={buttonClasses}
            disabled={!showCheckmark}
          >
            {buttonText}
          </OutlinedButton>
        ),
      [buttonType, theme, loading, inlineButton, buttonText, showCheckmark]
    );

    const errorSpan = (
      <span
        className={classes([
          styles.error,
          ...(error === '' || !error ? [styles.hideError] : []),
          ...(inlineButton ? [styles.hideOnMobile] : []),
        ])}
        role="alert"
        aria-atomic={true}
      >
        {error}
      </span>
    );

    const privacyCheckbox = (
      <PrivacyCheckbox
        className={classes([variantStyles, styles.checkbox])}
        defaultChecked={isAutoOptedIn}
        onChange={onCheckboxChange}
        countryCode={countryCode}
      />
    );

    const privacyDisclaimer = (
      <PrivacyDisclaimer
        className={classes([variantStyles, styles.disclaimer])}
        countryCode={countryCode}
      />
    );

    if (inlineButton) {
      return (
        <Form onSubmit={submitForm} noValidate={true} className={formClasses}>
          <div className={styles.inlineEmailFormContainer}>
            <div className={styles.privacyContainer}>
              {!hidePrivacyPolicy && (
                <div className={styles.inlinePrivacyPolicy}>
                  {privacyCheckbox}
                  {privacyDisclaimer}
                </div>
              )}
            </div>
            <div className={styles.inlineEmailComponentsContainer}>
              <div className={classes([styles.textInputContainerBoxTheme])}>
                {textInput}
                {errorSpan}
              </div>
              <div className={styles.hideOnMobile}>{submitButton}</div>
              <div className={styles.mobileButton}>{submitButton}</div>
            </div>
            <div className={styles.hideOnMobile}>{children}</div>
            <div className={styles.mobileChildren}>{children}</div>
          </div>
        </Form>
      );
    }

    return (
      <Form
        onSubmit={submitForm}
        noValidate={true}
        className={classes([formClasses])}
      >
        <div className={classes([styles.boxTheme])}>
          {!hidePrivacyPolicy && (
            <div className={styles.standardPrivacyPolicy}>
              {privacyCheckbox}
              {privacyDisclaimer}
            </div>
          )}
          {textInput}
          {submitButton}
          {errorSpan}
        </div>
      </Form>
    );
  }
);
